import { SWR_CACHE_PREFIX, evictSwrCacheEntries } from './swrCacheEviction'

/**
 * Keys that are safe to drop when localStorage is full even after eviction.
 * Only the best-effort SWR cache is droppable. Every other key routed through
 * this wrapper (session, keepMeLoggedIn, pkce_state, pkce_code_verifier) is
 * load-bearing — its write must surface a failure rather than be silently lost.
 */
const isDroppableKey = (key: string): boolean => key.startsWith(SWR_CACHE_PREFIX)

/**
 * Thin wrapper around `localStorage` with automatic QuotaExceededError recovery.
 * On quota error: evicts all SWR cache entries and retries once. If the retry
 * still fails, best-effort (SWR) writes are dropped with a warning while
 * load-bearing session/auth writes propagate the error. Re-throws non-quota
 * errors immediately.
 */
export const ippLocalStorage = {
  setItem: (key: string, value: string): void => {
    try {
      localStorage.setItem(key, value)
    } catch (err) {
      if (!(err instanceof DOMException && err.name === 'QuotaExceededError')) {
        throw err
      }

      // Evict all SWR entries to free space, then retry once.
      evictSwrCacheEntries({ maxEntriesToEvict: Infinity })
      try {
        localStorage.setItem(key, value)
      } catch (retryErr) {
        // Eviction did not free enough. Never silently drop a load-bearing write:
        // let session/auth failures surface. Best-effort cache writes are dropped.
        if (isDroppableKey(key)) {
          // eslint-disable-next-line no-console
          console.warn('[localStorage] Dropped non-critical write after quota eviction:', key)
          return
        }
        throw retryErr
      }
    }
  },
}
